import { useState, useCallback } from "react";

interface UseAnimationSequenceReturn {
  currentStep: number;
  nextStep: () => void;
  isStepActive: (step: number) => boolean;
  isStepCurrent: (step: number) => boolean;
  isComplete: boolean;
  reset: () => void;
}

/**
 * Hook para manejar secuencias de animación basadas en eventos.
 * Cada paso se activa cuando el anterior llama a nextStep().
 *
 * @param totalSteps - Número total de pasos en la secuencia
 * @param isActive - Si la secuencia está activa (default: true)
 *
 * @example
 * const { currentStep, nextStep, isStepActive, isComplete } = useAnimationSequence(3);
 *
 * // Step 0 se activa automáticamente cuando isActive=true
 * // Cuando step 0 termina, llama nextStep()
 * // Step 1 se activa, y así sucesivamente
 */
export const useAnimationSequence = (
  totalSteps: number,
  isActive = true
): UseAnimationSequenceReturn => {
  // Estado interno siempre empieza en 0
  const [internalStep, setInternalStep] = useState(0);

  // El paso efectivo es -1 si no está activo, sino usa el paso interno
  const currentStep = isActive ? internalStep : -1;

  const nextStep = useCallback(() => {
    setInternalStep((prev) => prev + 1);
  }, []);

  const isStepActive = useCallback(
    (step: number) => currentStep >= step,
    [currentStep]
  );

  const isStepCurrent = useCallback(
    (step: number) => currentStep === step,
    [currentStep]
  );

  const isComplete = isActive && internalStep >= totalSteps;

  const reset = useCallback(() => {
    setInternalStep(0);
  }, []);

  return {
    currentStep,
    nextStep,
    isStepActive,
    isStepCurrent,
    isComplete,
    reset,
  };
};
